import React from 'react';
import Layout from './Layout';
import { motion } from 'framer-motion';
import Solow from '../assets/Solow.png';
import FitnessVisual from '../assets/FitnessVisual.png';

const AAticleFitnessEconomy = () => {
  

  const sections = [
    {
      id: 1,
      title: "The Solow Growth Model in Economics",
      text: `In economics, the Solow Growth Model is a fundamental theory that explains how economies grow over time. Developed by Robert Solow in the 1950s, it shows how capital investment, labor force growth, and technological advancement come together to drive economic growth.

The model suggests that an economy's output (GDP) depends on its capital stock (machines, buildings), labor force (workers), and technology level (knowledge, efficiency). The key insight is that while adding more capital or labor can increase output, there's a point of diminishing returns - but technological progress can help overcome this limit.

Below is a visualization of the model, showing how investment and depreciation affect capital accumulation:`,
imageSpace: (
  <div className="w-full flex justify-center">
    <img 
      src={Solow} 
      alt="Fitness Growth Model"
      className="w-2/3 sm:w-1/2 md:w-2/3 object-contain rounded-lg"
    />
  </div>
),
      explanation: "The Solow Growth Model demonstrates how investment, depreciation, and technological progress affect economic growth."
    },
    {
      id: 2,
      title: "Introduction: The Economics of Muscle Growth",
      text: `Let's imagine your body is like a little country, and your muscles are the factories that produce strength. The Solow Growth Model, which economists use to explain how countries grow over time, can help us understand how your muscles grow. In this story, we’ll think about how different factors (like exercise, food, and rest) affect the size of your muscle “economy” and what happens when you change these factors.

The Basics: Inputs That Build Muscle

Just like a country needs workers, machines, and ideas to grow its economy, your muscles need three key things to grow:

Exercise (Capital) – This is the effort you put into training your muscles. Think of it like building more factories to produce strength.

Nutrition (Labor) – Your body needs energy and building blocks (like protein) to keep those factories running and growing.

Rest and Recovery (Technology) – This is the secret ingredient. Just like a country needs new inventions to get more output from its factories, your muscles need rest to repair and grow stronger.

These three things determine how much muscle your body can build. Now let’s see how they work together and what happens if you change them.`,
explanation: "Introducing the key inputs that drive muscle growth."    
},
    {
      id: 3,
      title: "The Steady State: Your Muscle Level Goal",
      text: `In the Solow Model, there's something called a steady state, which is the level a country's economy will reach if it keeps doing the same things over time. For your muscles, the steady state is the size your muscles will naturally reach if you keep your current habits the same.

If you don't exercise much, your muscles will stay small because you're not building many "factories."

If you eat poorly, your muscles won't grow because there's not enough energy to run the factories.

If you don't rest, your muscles won't repair and grow.

Your body is always trying to reach that steady state. If you work out more or change your diet, your steady state will shift, meaning your muscles will grow to a new size over time.`,
      explanation: "Understanding how your body reaches and maintains a certain muscle mass level."
    },
    {
      id: 4,
      title: "How to Change Your Muscle Steady State",
      text: `Let's see what happens when you change the key variables in your muscle-building formula.

1. Increasing Exercise (More Factories)

Imagine you start lifting weights more often. This is like a country building more factories. At first, your muscles will grow quickly because you're doing more work than before. But over time, the growth will slow down. Why? Because your body will reach a new steady state where your muscles are big enough to handle the work you're doing.

If you keep increasing exercise but don't eat enough or rest, your muscles won't grow as much as they could. It's like having factories without enough workers or tools.

2. Improving Nutrition (More Workers)

Now let's say you start eating more protein and healthy food. This is like giving your factories more workers and materials. Suddenly, your factories can produce more strength! Your muscles will grow faster and bigger because they have the fuel they need.

But again, there's a limit. If you eat too much without exercising, your body will store the extra energy as fat instead of muscle. You need a balance between exercise and nutrition.

3. Adding Rest and Recovery (Better Technology)

Finally, let's talk about rest. When you sleep and take breaks between workouts, your body repairs muscle fibers and makes them stronger. This is like giving your factories better machines that can produce more strength with less effort.

If you don't rest, your muscles will get tired and break down. You won't reach your potential steady state because your factories are worn out.`,
      imageSpace: (
        <div className="w-full flex justify-center">
          <img 
            src={FitnessVisual} 
            alt="Fitness Growth Model"
            className="w-2/3 sm:w-1/2 md:w-1/3 object-contain rounded-lg"
          />
        </div>
      ),
      explanation: "Learning how to manipulate the variables that affect muscle growth."
    },
    {
      id: 5,
      title: "The Role of Technology: Long-Term Gains",
      text: `In the Solow Model, technology is what makes a country's economy grow beyond its steady state. For your muscles, this means finding smarter ways to train. Maybe you learn new exercises, improve your form, or discover better recovery methods. These are like new inventions that help your muscle factories work more efficiently.

With better "technology" (smarter training), you can push past your current steady state and reach new levels of muscle growth.`,
      explanation: "Understanding how training innovation leads to continued progress."
    },
    {
      id: 6,
      title: "Diminishing Returns: Why Progress Slows Down",
      text: `When you first start working out, your muscles grow quickly. This is because your body is far from its steady state. But as you get closer to your potential, growth slows down. This is called diminishing returns in the Solow Model.

Think of it like this:

The first time you build a factory, it's a huge boost to your economy.
The second factory helps, but not as much as the first.
By the time you've built 100 factories, adding one more doesn't make a big difference.

The same thing happens with your muscles. When you're a beginner, every workout makes a big difference. But as you get stronger, each workout adds less and less muscle. You need to keep improving your "technology" (training methods) to keep growing.`,
      explanation: "Explaining why muscle growth becomes more challenging over time."
    },
    {
      id: 7,
      title: "Conclusion: Reaching Your Muscle Potential",
      text: `The Solow Growth Model teaches us that growth depends on how much you invest in your inputs (exercise, nutrition, and rest) and how efficiently you use them. If you want bigger muscles, you need to:

Exercise regularly to build more factories.
Eat well to give your factories the fuel they need.
Rest and recover to improve your factory's efficiency.

Remember, there's always a steady state your muscles are trying to reach. If you change your habits, you change your steady state. And if you want to go beyond that steady state, you need to keep learning and improving your training "technology." I suggest you start tracking what you are doing now and make small changes to see how your body responds and go from there.

Just like in the Solow Model, long-term muscle growth comes from consistent effort, balanced inputs, and smart innovations (improvements in recovery through better sleep, food, less stress, and hydration), do these well and you will go far!`,
      explanation: "Summarizing the key lessons from applying the Solow Model to understanding fitness."
    }
  ];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div>
      <Layout />
      <div className="w-full min-h-screen bg-gradient-to-b from-white to-white text-black pt-20">
        <div className="max-w-screen-lg mx-auto p-4">
          {/* Add Table of Contents */}
          <div className="mb-12 flex justify-center">
            <div className="bg-gray-50 p-6 rounded-lg shadow-sm max-w-[600px] w-full">
              <h2 className="text-2xl font-bold mb-4 text-center">Table of Contents</h2>
              <div className="space-y-2">
                {sections.map(section => section.title && (
                  <div 
                    key={section.id} 
                    className="cursor-pointer hover:scale-110 transition-colors text-center"
                    onClick={() => scrollToSection(`section-${section.id}`)}
                  >
                    {section.id}. {section.title}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-16">
            {sections.map(({ id, title, text, imageSpace, explanation }, index) => (
              <motion.div 
                key={id}
                initial={{ opacity: 1, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1.2, delay: index * 0.3 }} // Increased duration from 0.8 to 1.2, delay from 0.2 to 0.3
                className="flex flex-col gap-6"
                id={`section-${id}`}
              >
                <h2 className="text-3xl font-bold">{title}</h2>
                <div className="text-lg whitespace-pre-wrap">{text}</div>
                {imageSpace && (
                  <div className="h-auto max-w-full rounded-lg">
                    {imageSpace}
                  </div>
                )}
                {explanation && (
                  <p className="text-gray-600 italic">{explanation}</p>
                )}
              </motion.div>
            ))}
          </div>

          {/* Add Back to Top Button */}
          <div className="flex justify-center mt-12 mb-8">
            <button
              onClick={scrollToTop}
              className="text-white font-semibold py-2 px-4 rounded-lg shadow transition-colors duration-300 hover:scale-110"
              style={{ background: 'rgb(1, 50, 32)' }}
            >
              Back to Top
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AAticleFitnessEconomy;